import React, { useEffect, useState, useCallback } from "react";
import {
  X,
  ArrowRight,
  SlidersVertical,
  AlignLeft,
  ChevronDown,
} from "lucide-react";
import dayjs from "dayjs";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import homeIcon from "../assets/images/homeIcon.svg";
import savingsIcon from "../assets/images/savingsProd.svg";
import inactive from "../assets/icons/unverified.svg";
import pendingIcon from "../assets/icons/pending-circle.svg";
import approvedIcon from "../assets/icons/approved-icon.svg";
import rejected from "../assets/icons/unverified.svg";
import CurrencyFormatter from "../utils/currencyFormatter";
import { useNavigate } from "react-router-dom";
import StringFormatter from "../utils/stringFormatter";

export const getDateRange = (filter) => {
  const endDate = dayjs();
  let startDate;

  if (typeof filter === "string") {
    switch (filter) {
      case "Last 24 hours":
        startDate = endDate.subtract(1, "day");
        break;
      case "Last 7 days":
        startDate = endDate.subtract(1, "week");
        break;
      case "Last 30 days":
        startDate = endDate.subtract(1, "month");
        break;
      case "Last 3 months":
        startDate = endDate.subtract(3, "month");
        break;
      case "Last 6 months":
        startDate = endDate.subtract(6, "month");
        break;
      case "Last 12 months":
        startDate = endDate.subtract(1, "year");
        break;
      case "All":
        startDate = dayjs("1970-01-01");
        break;
      default:
        throw new Error("Invalid filter string");
    }
    return { startDate, endDate };
  }

  if (filter?.startDate && filter?.endDate) {
    return {
      startDate: dayjs(filter.startDate),
      endDate: dayjs(filter.endDate),
    };
  }

  throw new Error("Invalid filter input");
};

export const filterDataByDate = (data, filter) => {
  let startDate, endDate;

  if (typeof filter === "string") {
    ({ startDate, endDate } = getDateRange(filter));
  } else if (typeof filter === "object" && filter.startDate && filter.endDate) {
    startDate = dayjs(filter.startDate);
    endDate = dayjs(filter.endDate);
  } else {
    return data;
  }

  return data.filter((item) => {
    const itemDate = dayjs(item.createdAt);
    return itemDate.isAfter(startDate) && itemDate.isBefore(endDate);
  });
};

const InvestmentHistoryAll = ({
  transactionsComplete,
  openAll,
  setOpenAll,
  onOpenSingle,
  setIsFromAllTr,
  onDateRangeOpen,
  customStart,
  customEnd,
  setCustomEnd,
  setCustomStart,
  title,
  type,
}) => {
  const [filtered, setFiltered] = useState(transactionsComplete);
  const [selected, setSelected] = useState("All");
  const [openFilter, setOpenFilter] = useState(false);
  const [openSort, setOpenSort] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [selectedSort, setSelectedSort] = useState("Newest");
  const [openTransactionType, setOpenTransactionType] = useState(false);
  const [openDateRange, setOpenDateRange] = useState(false);

  const navigate = useNavigate();

  const formatter = new CurrencyFormatter();

  const applyFilters = (transactionsComplete, dateRange, selectedTypes) => {
    const dateFiltered = filterDataByDate(transactionsComplete, dateRange);

    if (
      !selectedTypes ||
      selectedTypes.includes("All") ||
      selectedTypes.length === 0
    ) {
      return dateFiltered;
    }

    const selectedSet = new Set(selectedTypes.map((t) => t.toLowerCase()));

    return dateFiltered.filter((item) =>
      selectedSet.has(item?.productId?.investmentType)
    );
  };

  const handleSortChange = (sortOption) => {
    setSelectedSort(sortOption);
    let sorted = [...transactionsComplete];

    switch (sortOption) {
      case "Newest":
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "Highest to Lowest":
        sorted.sort(
          (a, b) =>
            parseFloat(b?.productId?.value?.$numberDecimal) -
            parseFloat(a?.productId?.value?.$numberDecimal)
        );
        break;
      case "Lowest to Highest":
        sorted.sort(
          (a, b) =>
            parseFloat(a?.productId?.value?.$numberDecimal) -
            parseFloat(b?.productId?.value?.$numberDecimal)
        );
        break;
      default:
        break;
    }

    setFiltered(sorted);
  };

  const handleDateFilterChange = useCallback(
    (range) => {
      setCustomStart(null);
      setCustomEnd(null);
      setSelected(range);
      const result = applyFilters(transactionsComplete, range, selectedFilter);
      setFiltered(result);
    },
    [transactionsComplete, selectedFilter, setCustomStart, setCustomEnd]
  );

  useEffect(() => {
    if (!customEnd && !customStart) return;
    if (customEnd && customStart) {
      handleDateFilterChange({ startDate: customStart, endDate: customEnd });
    }
  }, [customEnd, customStart, handleDateFilterChange]);

  const handleTypeFilterChange = (type) => {
    setSelectedFilter(type);
    const result = applyFilters(transactionsComplete, selected, type);
    setFiltered(result);
  };

  const handleCheckboxChange = (type) => {
    let updatedTypes;

    if (selectedFilter.includes(type)) {
      updatedTypes = selectedFilter.filter((t) => t !== type);
    } else {
      updatedTypes = [...selectedFilter, type];
    }

    setSelectedFilter(updatedTypes);
    handleTypeFilterChange(updatedTypes);
  };

  const handleDateRange = () => {
    onDateRangeOpen?.();
  };
  const filterOptions = ["All", "Savings", "Commodities", "Property", "Other"];
  const sortOptions = ["Newest", "Highest to Lowest", "Lowest to Highest"];

  const string = new StringFormatter();
  return (
    <>
      <Dialog open={openAll} onOpenChange={setOpenAll}>
        <DialogTrigger asChild></DialogTrigger>
        <DialogContent className="max-w-[780px] rounded-2xl p-6  flex-1 flex flex-col">
          <DialogClose asChild>
            <Button
              variant="ghost"
              onClick={() => setOpenAll(false)}
              className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
            >
              <X size={18} className="text-gray-700" />
            </Button>
          </DialogClose>

          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-800">
              {title}
            </DialogTitle>

            <div className="flex justify-end gap-5 mr-12">
              <div className="flex gap-5">
                <DropdownMenu
                  open={openFilter}
                  onOpenChange={(open) => setOpenFilter(open)}
                  modal={false}
                >
                  <DropdownMenuTrigger>
                    <Button
                      onClick={() => setOpenFilter((prev) => !prev)}
                      variant="outline"
                      size="icon"
                      className="rounded-full px-14"
                    >
                      Filter{" "}
                      <SlidersVertical className="w-5 h-5 text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="rounded-xl p-1 w-[318px] max-w-none p-3">
                    <div className="border rounded-2xl w-full overflow-hidden mb-3">
                      <div
                        onClick={() => setOpenTransactionType((prev) => !prev)}
                        className="w-full cursor-pointer flex items-center justify-between py-4 px-4 hover:border-brand-primary transition-colors"
                      >
                        <div className="text-left">
                          <div className="font-semibold text-sm">
                            Transaction Type
                          </div>
                          <div className="text-gray-600 text-sm">All</div>
                        </div>
                        <ChevronDown
                          className={`text-gray-400 transition-transform duration-300 ${
                            openTransactionType ? "rotate-180" : ""
                          }`}
                        />
                      </div>

                      {openTransactionType && (
                        <div className="border-t px-4 py-3 bg-white">
                          {filterOptions.map((type) => (
                            <label
                              key={type}
                              className="flex items-center gap-2 py-3"
                            >
                              <input
                                type="checkbox"
                                value={type}
                                checked={selectedFilter.includes(type)}
                                onChange={() => handleCheckboxChange(type)}
                                className="accent-rose-600 w-5 h-5 rounded"
                              />
                              <span className="text-gray-700 text-sm">
                                {type}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="border rounded-2xl w-full overflow-hidden">
                      <div
                        onClick={() => setOpenDateRange((prev) => !prev)}
                        className="w-full cursor-pointer flex items-center justify-between py-4 px-4 hover:border-brand-primary transition-colors"
                      >
                        <div className="text-left">
                          <div className="font-semibold text-sm">
                            Date range
                          </div>
                          <div className="text-gray-600 text-sm">
                            {"All"}
                            {}
                          </div>
                        </div>
                        <ChevronDown
                          className={`text-gray-400 transition-transform duration-300 ${
                            openDateRange ? "rotate-180" : ""
                          }`}
                        />
                      </div>

                      {openDateRange && (
                        <div className="border-t px-4 py-3 bg-white flex flex-col">
                          {[
                            "Last 24 hours",
                            "Last 7 days",
                            "Last 30 days",
                            "Last 3 months",
                            "Last 6 months",
                            "Last 12 months",
                          ].map((type) => (
                            <label
                              key={type}
                              className="flex items-center gap-2 py-2"
                            >
                              <input
                                onChange={(e) =>
                                  handleDateFilterChange(e.target.value)
                                }
                                type="radio"
                                value={type}
                                name="dateRange"
                                className="accent-rose-600 w-5 h-5 rounded"
                              />
                              <span className="text-gray-500 font-semibold">
                                {type}
                              </span>
                            </label>
                          ))}

                          <div className="border-t mt-2 mb-3"></div>

                          <button
                            type="button"
                            className="py-4 text-sm font-bold text-black border border-gray-300 rounded-full bg-white hover:bg-gray-100"
                            onClick={handleDateRange}
                          >
                            Custom Date Range
                          </button>
                        </div>
                      )}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu
                  open={openSort}
                  onOpenChange={(open) => setOpenSort(open)}
                  modal={false}
                >
                  <DropdownMenuTrigger>
                    <Button
                      onClick={() => setOpenSort((prev) => !prev)}
                      variant="outline"
                      size="icon"
                      className="rounded-full px-14 flex items-center gap-2"
                    >
                      Sort
                      <AlignLeft className="w-5 h-5 text-gray-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="rounded-xl w-full max-w-[218px]">
                    <DropdownMenuLabel>Filter by type</DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-400" />

                    {sortOptions.map((option) => (
                      <DropdownMenuItem
                        key={option}
                        onClick={() => {
                          handleSortChange(option);
                          setOpenSort(false);
                        }}
                        className={`cursor-pointer ${
                          selectedSort === option
                            ? "bg-gray-100 font-semibold"
                            : ""
                        }`}
                      >
                        {option}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </DialogHeader>

          {
            <div className="mt-4 flex flex-col space-y-3 px-1 max-h-72 overflow-y-auto">
              {transactionsComplete &&
                filtered.map((transaction) => {
                  return (
                    <div
                      key={transaction._id}
                      className="grid grid-cols-6 space-x-[60px] py-1.5"
                    >
                      <div className="flex items-center gap-4">
                        {transaction?.productId?.investmentType ===
                        "property" ? (
                          <img src={homeIcon} alt="" className="w-11 h-11" />
                        ) : (
                          <img src={savingsIcon} alt="" className="w-11 h-11" />
                        )}
                        <div>
                          <div className="text-gray-900 font-medium">
                            {transaction?.productId?.name}
                          </div>
                          <div className="text-gray-500 text-sm">
                            {string.capitalizeFirst(
                              transaction?.productId?.investmentType
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-left">
                        <div>
                          <p className="text-gray-900 font-medium">Value</p>
                          <p className="text-gray-500 text-sm break-words whitespace-normal w-20">
                            {formatter.format(
                              transaction?.productId?.value?.$numberDecimal,
                              transaction?.productId?.assetId?.asset?.toUpperCase()
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-left">
                        <div>
                          <p className="text-gray-900 font-medium">Risk</p>
                          <p className="text-gray-500 text-sm">
                            {string.capitalizeFirst(
                              transaction?.productId?.risk
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-left">
                        <div>
                          <p className="text-gray-900 font-medium">ROI</p>
                          <p className="text-gray-500 text-sm">
                            {transaction?.productId?.roi?.$numberDecimal} %
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center text-xl px-18 justify-center">
                        {type === "investments" ? (
                          <div
                            className="flex items-center justify-center px-3.5 py-1 rounded-full"
                            style={{
                              backgroundColor:
                                transaction?.productId?.status === "pending"
                                  ? "#FFF2EB"
                                  : transaction?.productId?.status ===
                                      "inactive"
                                    ? "#F9E8E8"
                                    : "#E8F9F6",
                            }}
                          >
                            <img
                              src={
                                transaction?.productId?.status === "pending"
                                  ? pendingIcon
                                  : transaction?.productId?.status ===
                                      "inactive"
                                    ? inactive
                                    : approvedIcon
                              }
                              alt=""
                              className="mr-2 h-4 w-4"
                            />
                            <p
                              className="text-[14px]"
                              style={{
                                color:
                                  transaction?.productId?.status === "pending"
                                    ? "#974D21"
                                    : transaction?.productId?.status ===
                                        "inactive"
                                      ? "#75100E"
                                      : "#0E7563",
                              }}
                            >
                              {string.capitalizeFirst(
                                transaction?.productId?.status
                              )}
                            </p>
                          </div>
                        ) : (
                          <div
                            className="flex items-center justify-center px-3.5 py-1 rounded-full"
                            style={{
                              backgroundColor:
                                transaction?.status === "pending"
                                  ? "#FFF2EB"
                                  : transaction?.status === "rejected"
                                    ? "#FBEAEA"
                                    : transaction?.status === "inactive"
                                      ? "#F9E8E8"
                                      : "#E8F9F6",
                            }}
                          >
                            <img
                              src={
                                transaction?.status === "pending"
                                  ? pendingIcon
                                  : transaction?.status === "rejected"
                                    ? rejected
                                    : transaction?.status === "inactive"
                                      ? inactive
                                      : approvedIcon
                              }
                              alt=""
                              className="mr-2 h-4 w-4"
                            />
                            <p
                              className="text-[14px]"
                              style={{
                                color:
                                  transaction?.status === "pending"
                                    ? "#974D21"
                                    : transaction?.status === "rejected"
                                      ? "#D93025"
                                      : transaction?.status === "inactive"
                                        ? "#75100E"
                                        : "#0E7563",
                              }}
                            >
                              {string.capitalizeFirst(transaction?.status)}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-end">
                        {type === "investments" ? (
                          <Button
                            onClick={() => {
                              navigate("/invest/product", {
                                state: {
                                  productType: "asset2",
                                  productId: transaction?.productId?._id,
                                  ledgerId: transaction?._id,
                                  ledgerItem: transaction,
                                },
                              });
                            }}
                            variant="outline"
                            size="icon"
                            className="rounded-full"
                          >
                            <ArrowRight className="w-5 h-5 text-gray-500" />
                          </Button>
                        ) : (
                          <Button
                            onClick={() => {
                              navigate("/invest/product", {
                                state: {
                                  productType: "asset2",
                                  productId: transaction?.productId?._id,
                                  applicationId: transaction?._id,
                                },
                              });
                            }}
                            variant="outline"
                            size="icon"
                            className="rounded-full"
                          >
                            <ArrowRight className="w-5 h-5 text-gray-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          }
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InvestmentHistoryAll;
